import { useMemo } from 'react';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';

import createStyles from '@guestyci/foundation/createStyles';
import TextField from '@guestyci/foundation/TextField';

import CurrencySelect from 'components/CurrencySelect';

import useGetWebsiteSettings from 'hooks/useGetWebsiteSettings';
import t from '@guestyci/localize/t.macro/t.macro';
import SelectLanguageDropdown from 'components/SelectLanguageDropdown';
import useGetPathToNavigate from 'hooks/useGetPathToNavigate';

const useStyles = createStyles(({ breakpoints: { create } }) => ({
  root: {
    background: ({ background }) => background || '#fff',
    height: 100,
    display: 'flex',
    padding: 20,
    alignItems: 'center',
  },
  navigation: {
    marginLeft: 'auto',
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'center',
    width: 120,
    [create('xs')]: {
      gap: 10,
    },
    [create('md')]: {
      gap: 30,
    },
    '& > a': {
      fontSize: 14,
      color: ({ textColor }) => textColor || '#212121',
    },
  },
  logo: {
    [create('xs')]: {
      marginLeft: 0,
      marginRight: 0,
      height: 20,
    },
    [create('md')]: {
      marginRight: -120,
      marginLeft: 'auto',
      height: 60,
      width: 'auto',
    },
    '& > img': {
      width: 'auto',
      height: '100%',
    },
  },
}));

const LogoWithOrWithourExternalWebsiteUrl = ({
  className,
  externalLinkingOn,
  externalWebsiteUrl,
  logoUrl,
  search,
  unifiedDomain,
}) => {
  const { isTranslationsEnabled } = useGetPathToNavigate();
  const { locale = 'en' } = useRouteMatch('/:locale')?.params || {};
  const path = isTranslationsEnabled ? `/${locale}` : '/';
  if (unifiedDomain) {
    return (
      <a className={className} href="/" rel="noreferrer">
        <img src={logoUrl} alt="logo" />
      </a>
    );
  }
  if (externalWebsiteUrl && externalLinkingOn) {
    return (
      <a className={className} href={externalWebsiteUrl} target="_blank" rel="noreferrer">
        <img src={logoUrl} alt="logo" />
      </a>
    );
  }
  return (
    <Link
      className={className}
      to={{
        pathname: path,
        search,
      }}
    >
      <img src={logoUrl} alt="logo" />
    </Link>
  );
};

const Header = () => {
  const { isTranslationsEnabled } = useGetPathToNavigate();
  const { locale = 'en' } = useRouteMatch('/:locale')?.params || {};
  const path = isTranslationsEnabled ? `/${locale}` : '';
  const { data, isLoading, isSuccess } = useGetWebsiteSettings();

  const {
    contentConfiguration: {
      logo: logoImage = '',
      colorTheme: { colors = {} } = {},
      externalWebsiteUrl = null,
      sections,
    } = {},
    displayOptions: { externalLinkingOn = false } = {},
    enginesData: { unifiedDomain = false } = {},
  } = data || {};

  const { search } = useLocation();
  const { root, navigation, logo } = useStyles({
    background: colors?.headerBackground,
    textColor: colors?.headerLink,
  });

  const withContact = useMemo(() => !!sections?.CONTACT?.active, [sections]);
  const withTerms = useMemo(() => !!sections?.TERMS?.active, [sections]);

  if (!isSuccess || isLoading) {
    return <header className={root} />;
  }
  return (
    <header className={root}>
      <LogoWithOrWithourExternalWebsiteUrl
        externalWebsiteUrl={externalWebsiteUrl}
        externalLinkingOn={externalLinkingOn}
        logoUrl={logoImage}
        className={logo}
        search={search}
        unifiedDomain={unifiedDomain}
      />
      <div className={navigation}>
        <SelectLanguageDropdown />
        <CurrencySelect />
        {withContact && (
          <Link to={`${path}/contact`}>
            <TextField>{t('Contact')}</TextField>
          </Link>
        )}
        {withTerms && (
          <Link to={`${path}/terms`}>
            <TextField>{t('Terms')}</TextField>
          </Link>
        )}
      </div>
    </header>
  );
};

export default Header;
